import React, { FC, useState, useEffect } from 'react';
import cx from 'classnames';
import styles from './styles.module.css';
import * as translations from '../strings';
import { getLangKey } from 'appJS/models/Language';
import { getNewReactions, Emoji, UserReaction } from './getNewReactions';
import { PickerButton } from './PickerButton';
import { CommentReactions } from './CommentReactions';
import { UpdateComment } from '../../../../../../../../../shared/updateComment';

const strings = translations[getLangKey()];

type Props = {
  reactions: string;
  id: number;
  isPrivate: boolean;
  currentUser: { id: number };
  users: { id: number; name: string }[];
  updateComment: UpdateComment;
  getContainer: () => HTMLDivElement;
  taskId: number;
  apiDomain: string;
  isAdminView: boolean;
  projectId: number;
  showReactions?: boolean;
  showPicker?: boolean;
  pickerPlacement?: 'leftBottom' | 'bottomRight';
};

const Reactions: FC<Props> = ({
  currentUser,
  getContainer,
  users,
  updateComment,
  reactions,
  id: commentId,
  isPrivate,
  taskId,
  apiDomain,
  isAdminView,
  projectId,
  showReactions = true,
  showPicker = true,
  pickerPlacement = 'leftBottom'
}) => {
  const [commentReactions, setCommentReactions] = useState<{
    [key: string]: { name: string; userReactions: UserReaction[] };
  }>(JSON.parse(reactions || '{}'));
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);

  const updateReactions = ({
    reactionKey,
    emoji
  }: {
    reactionKey?: string;
    emoji?: Emoji;
  }) => {
    const newReactions = getNewReactions({
      commentReactions,
      reactionKey,
      emoji,
      currentUserId: currentUser.id
    });
    updateComment({
      commentId,
      commentEvent: {
        type: 'reaction',
        reactions: JSON.stringify(newReactions)
      },
      taskId
    });
    setCommentReactions(newReactions);
  };

  const handleEmojiSelect = (emoji: Emoji) => {
    updateReactions({ emoji });
    setPopoverOpen(false);
  };

  useEffect(() => {
    if (reactions !== JSON.stringify(commentReactions)) {
      setCommentReactions(JSON.parse(reactions || '{}'));
    }
  }, [reactions]);

  if (!showPicker && !Object.keys(commentReactions).length) return null;

  return (
    <div
      className={cx(styles.container, {
        [styles.isPrivate]: isPrivate,
        privateComment: isPrivate,
        [styles.pickerOnly]: !showReactions
      })}
    >
      {showReactions && (
        <CommentReactions
          currentUser={currentUser}
          getContainer={getContainer}
          users={users}
          updateReactions={updateReactions}
          commentReactions={commentReactions}
        />
      )}
      {showPicker && (
        <PickerButton
          getContainer={getContainer}
          onEmojiSelect={handleEmojiSelect}
          tooltipTitle={strings.addReaction}
          placement={pickerPlacement}
          popoverOpen={popoverOpen}
          setPopoverOpen={setPopoverOpen}
          reactionPicker={showReactions}
          headerPicker={!showReactions}
          apiDomain={apiDomain}
          isAdminView={isAdminView}
          projectId={projectId}
        />
      )}
    </div>
  );
};

export default Reactions;
